/**
 * App icon badge (Jason, 2026-10-19): mirror the bell's unread count onto the
 * home-screen icon on the native apps, and clear it when everything is read.
 *
 * Native only. On the web this is a no-op: every entry point checks
 * Capacitor.isNativePlatform() first, so the plugin's web shim is never even
 * requested. Every call is best effort: a missing plugin, a denied
 * permission, or an older installed app without the plugin just leaves the
 * icon as it was. Nothing here can affect the in-app notifications or the
 * push itself; the server sets the same number on each push
 * (functions/sendPushOnNotification.js) so the icon is right even while the
 * app is closed.
 *
 * The plugin is imported statically, like every other Capacitor plugin in
 * this app. The first build loaded it with a dynamic import() to keep it out
 * of the web bundle; on the first device test (2026-10-20) that import never
 * settled on iOS (the step log stopped right before it, no error, no
 * warning) while the route-level lazy chunks load fine. The plugin's own
 * entry module is a few hundred bytes, so there is nothing to save.
 */
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

let lastApplied = null;
let capabilityReportedFor = null; // uid the flag was written for this session

/**
 * Tell the server this install can manage the icon badge. The push only
 * carries a badge number for accounts that have reported this, so members on
 * an older build (no plugin, nothing to clear the number) never get a stale
 * badge stuck on their icon. Once per signed-in uid per session, best effort.
 *
 * Called on sign-in (NotificationContext) and not only from the count path:
 * the first build reported the flag only when the unread count CHANGED, so a
 * member who opened the app with nothing unread never wrote it, and the next
 * push while the app was closed arrived without a badge (owner, first device
 * test, 2026-10-20).
 */
export async function reportAppIconBadgeCapable() {
  if (!Capacitor.isNativePlatform()) return false;
  const user = auth.currentUser;
  if (!user) return false;
  if (capabilityReportedFor === user.uid) return true;
  // Step logs stay: the first device test (2026-10-20) wrote nothing and
  // warned nothing, so a silent hang between these awaits has to be visible
  // in the Xcode console.
  console.log('App icon badge: reporting capability for', user.uid);
  try {
    const { isSupported } = await Badge.isSupported();
    console.log('App icon badge: supported', isSupported);
    if (!isSupported) return false;
    await updateDoc(doc(db, 'users', user.uid), { appIconBadge: true });
    console.log('App icon badge: capability recorded');
    capabilityReportedFor = user.uid;
    return true;
  } catch (err) {
    console.warn('App icon badge capability not recorded:', err && err.message);
    return false;
  }
}

/** Set the icon badge to `count` (0 clears). Resolves to true when applied. */
export async function syncAppIconBadge(count) {
  if (!Capacitor.isNativePlatform()) return false;
  const n = Number.isFinite(Number(count)) && Number(count) > 0 ? Math.floor(Number(count)) : 0;
  // Not awaited: the capability write can hang offline or on a dead write
  // stream, and the icon must still follow the bell (review, 2026-10-20).
  reportAppIconBadgeCapable().catch(() => {});
  if (n === lastApplied) return true;
  try {
    const { isSupported } = await Badge.isSupported();
    if (!isSupported) return false;
    if (n === 0) await Badge.clear();
    else await Badge.set({ count: n });
    lastApplied = n;
    return true;
  } catch (err) {
    console.warn('App icon badge update failed:', err && err.message);
    return false;
  }
}
